import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * A minimalist custom cursor: a small dot + a slow-following ring.
 * Hidden on touch devices.
 */
export default function CustomCursor() {
    const [pos, setPos] = useState({ x: -100, y: -100 });
    const [ringPos, setRingPos] = useState({ x: -100, y: -100 });
    const [hover, setHover] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const isCoarse = window.matchMedia("(pointer: coarse)").matches;
        if (isCoarse) return;

        setVisible(true);
        const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });
        const onOver = (e) => {
            const t = e.target;
            const interactive = t.closest("a, button, [role='slider']");
            setHover(!!interactive);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseover", onOver);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseover", onOver);
        };
    }, []);

    // Ring follows with lag
    useEffect(() => {
        let raf;
        const loop = () => {
            setRingPos((prev) => ({
                x: prev.x + (pos.x - prev.x) * 0.15,
                y: prev.y + (pos.y - prev.y) * 0.15,
            }));
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [pos]);

    if (!visible) return null;

    return (
        <>
            <motion.div
                aria-hidden="true"
                className="fixed pointer-events-none z-[70] mix-blend-difference"
                style={{
                    left: pos.x,
                    top: pos.y,
                    translateX: "-50%",
                    translateY: "-50%",
                }}
            >
                <motion.div
                    animate={{ scale: hover ? 0 : 1 }}
                    transition={{ duration: 0.35 }}
                    className="w-1.5 h-1.5 bg-white rounded-full"
                />
            </motion.div>
            <motion.div
                aria-hidden="true"
                className="fixed pointer-events-none z-[69] mix-blend-difference"
                style={{
                    left: ringPos.x,
                    top: ringPos.y,
                    translateX: "-50%",
                    translateY: "-50%",
                }}
            >
                <motion.div
                    animate={{ scale: hover ? 2.2 : 1, borderColor: hover ? "#fff" : "rgba(255,255,255,0.9)" }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="w-9 h-9 rounded-full border border-white/90"
                />
            </motion.div>
        </>
    );
}
